import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type UserRow } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Modal } from '../../components/ui/Modal'
import { Label } from '../../components/ui/Label'
import { Select } from '../../components/ui/Select'

const ROLES = ['partner_admin', 'operator', 'viewer']

export function Users() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState({ email: '', role: 'operator' })

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<UserRow[]>('/api/v1/admin/users'),
  })

  const invite = useMutation({
    mutationFn: () =>
      api<UserRow>('/api/v1/admin/users/invite', {
        method: 'POST',
        body: JSON.stringify(draft),
      }),
    onSuccess: () => {
      setOpen(false)
      setDraft({ email: '', role: 'operator' })
      qc.invalidateQueries({ queryKey: ['admin', 'users'] })
    },
  })

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.users.title')}</h1>
        <Button variant="primary" onClick={() => setOpen(true)}>
          {t('admin.users.invite')}
        </Button>
      </header>

      {isLoading && <p className="text-sm text-text-muted">{t('common.loading')}</p>}

      {data && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-cream text-xs uppercase text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('admin.users.columns.email')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.users.columns.roles')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.users.columns.created')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((u) => (
                  <tr key={u.id} className="border-t border-warm-mist">
                    <td className="px-4 py-2">{u.email}</td>
                    <td className="px-4 py-2 text-xs">{u.roles.join(', ')}</td>
                    <td className="px-4 py-2 text-xs text-text-muted">
                      {u.created_at.slice(0, 10)}
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-6 text-center text-sm text-text-muted">
                      {t('common.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Modal
        open={open}
        title={t('admin.users.invite')}
        onClose={() => setOpen(false)}
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => invite.mutate()}>
              {t('admin.users.invite')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <Label>{t('admin.users.columns.email')}</Label>
            <Input
              type="email"
              value={draft.email}
              onChange={(e) => setDraft({ ...draft, email: e.target.value })}
            />
          </div>
          <div>
            <Label>{t('admin.users.columns.roles')}</Label>
            <Select value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  )
}
